import type { ConnectionDetail } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Target } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Section } from '@/components/section';
import { ErrorState, LoadingRows } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/**
 * Which properties (GA4 properties, Search Console sites) the connection's agents reach, ticked from what the
 * signed-in Google account sees. Saving checks the connection again, so its last check names what agents get.
 */
export function ConnectionScopeSection({ connection: c, workspaceId, className }: { connection: ConnectionDetail; workspaceId: string; className?: string }) {
  const signedIn = !!c.oauth?.connected;
  const choices = useQuery({ queryKey: qk.scopeChoices(c.id), queryFn: () => api.connections.scopeChoices(c.id), enabled: signedIn, retry: false });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (choices.data) setSelected(new Set(choices.data.selected));
  }, [choices.data]);

  const save = useApiMutation(
    async () => {
      const key = choices.data!.config;
      const rest = { ...c.config };
      delete rest[key];
      // Kept in the order Google lists them; nothing ticked means every property of the account.
      const chosen = choices.data!.choices.map((x) => x.value).filter((v) => selected.has(v));
      await api.connections.update(c.id, { config: chosen.length ? { ...rest, [key]: chosen } : rest });
      return api.connections.healthCheck(c.id);
    },
    { invalidate: [qk.connection(c.id), qk.connections(workspaceId)], success: t('Saved and checked') },
  );

  const toggle = (value: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  const changed = !!choices.data && (selected.size !== choices.data.selected.length || choices.data.selected.some((v) => !selected.has(v)));
  const list = choices.data?.choices ?? [];

  return (
    <Section
      icon={<Target />}
      title={t('Properties')}
      description={t('Agents only reach the ones you tick. None ticked: every property the Google account sees.')}
      className={className}
      actions={
        list.length > 0 && (
          <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!changed}>
            {t('Save')}
          </Button>
        )
      }
    >
      {!signedIn ? (
        <p className="text-sm text-muted-foreground">{t('Sign in with Google first; then the properties of the account appear here.')}</p>
      ) : choices.isPending ? (
        <LoadingRows rows={2} />
      ) : choices.isError ? (
        <ErrorState error={choices.error} onRetry={() => void choices.refetch()} />
      ) : list.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('The signed-in Google account sees no properties. Sign in with an account that has access.')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {list.length > 1 && choices.data.selected.length === 0 && (
            <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
              {t('Tick the properties this connection is for, so agents know which one is meant.')}
            </p>
          )}
          <ul className="flex flex-col gap-1">
            {list.map((choice) => (
              <li key={choice.value}>
                <label className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-muted">
                  <Checkbox checked={selected.has(choice.value)} onChange={() => toggle(choice.value)} />
                  <span className="min-w-0 flex-1 truncate">{choice.label ?? choice.value}</span>
                  {choice.label && <span className="shrink-0 font-mono text-xs text-muted-foreground">{choice.value}</span>}
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Section>
  );
}
